import React, { useContext, useEffect, useState } from "react";
import StepNavigation from "components/StepNavigation";
import FormStep from "components/FormStep";
import { FormDataContext } from "context/FormDataContext";
import { ApiDataContext } from "context/ApiDataContext";
import styled from "styled-components";
import useModal from "hooks/useModal";
import PrintModal from "components/Modals/Print";
import { useMsal, useAccount } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { callApiWithToken } from "fetch";
import { protectedResources } from "authConfig";
import Layout from "components/Layout";

function Apron() {
  const {
    currentStep,
    updateStep,
    jobType,
    currentOrganization,
    validatedSteps,
    setValidatedSteps,
    dataFetched,
    updateEmployment,
  } = useContext(FormDataContext);
  const { steps } = useContext(ApiDataContext);
  const { isVisible, toggleModal, activeModal } = useModal();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [validatedStepsLoaded, setValidatedeStepsLoaded] = useState(false);

  const fetchCurrentEmploymentOrCreateNew = () => {
    const data = {
      jobType: jobType,
      organization: currentOrganization[0],
      userName: accounts[0].name,
    };
    instance
      .acquireTokenSilent({
        scopes: protectedResources.addEmployment.scopes,
        account: account,
      })
      .then((response) => {
        callApiWithToken(
          response.accessToken,
          `${protectedResources.addEmployment.endpoint}`,
          "POST",
          data
        ).then((response) => updateEmployment(response));
      })
      .catch((error) => {
        // in case if silent token acquisition fails, fallback to an interactive method
        if (error instanceof InteractionRequiredAuthError) {
          if (account && inProgress === "none") {
            instance
              .acquireTokenPopup({
                scopes: protectedResources.addEmployment.scopes,
              })
              .then((response) => {
                callApiWithToken(
                  response.accessToken,
                  `${protectedResources.addEmployment.endpoint}`,
                  "POST",
                  data
                ).then((response) => updateEmployment(response));
              })
              .catch((error) => console.log(error));
          }
        }
      });
  };

  // Loads current employment from db or creates a new one
  useEffect(() => {
    if (account && inProgress === "none" && currentOrganization[0]) {
      fetchCurrentEmploymentOrCreateNew();
    }
  }, [account, inProgress, instance, currentOrganization]);

  useEffect(() => {
    if (account && inProgress === "none" && currentOrganization[0]) {
      const data = {
        jobType: jobType,
        organization: currentOrganization[0],
      };
      instance
        .acquireTokenSilent({
          scopes: protectedResources.validatedSteps.scopes,
          account: account,
        })
        .then((response) => {
          callApiWithToken(
            response.accessToken,
            `${protectedResources.validatedSteps.endpoint}`,
            "POST",
            data
          ).then((response) => {
            const validatedStepsFromDb = response.steps;
            let updatedValidatedSteps = {
              ...validatedSteps,
              ...validatedStepsFromDb,
            };
            setValidatedSteps(updatedValidatedSteps);
            setValidatedeStepsLoaded(true);
          });
        })
        .catch((error) => {
          // in case if silent token acquisition fails, fallback to an interactive method
          if (error instanceof InteractionRequiredAuthError) {
            if (account && inProgress === "none") {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.validatedSteps.scopes,
                })
                .then((response) => {
                  callApiWithToken(
                    response.accessToken,
                    `${protectedResources.validatedSteps.endpoint}`,
                    "POST",
                    data
                  ).then((response) => {
                    const validatedStepsFromDb = response.steps;
                    let updatedValidatedSteps = {
                      ...validatedSteps,
                      ...validatedStepsFromDb,
                    };
                    setValidatedSteps(updatedValidatedSteps);
                    setValidatedeStepsLoaded(true);
                  });
                })
                .catch((error) => console.log(error));
            }
          }
        });
    }
  }, [account, inProgress, currentOrganization]);

  // Update validated steps in the DB, runs everytime validatedSteps has been updated.
  // Could be defered for performance
  useEffect(() => {
    if (
      account &&
      inProgress === "none" &&
      currentOrganization[0] &&
      validatedStepsLoaded
    ) {
      const data = {
        jobType: jobType,
        organization: currentOrganization[0],
        steps: validatedSteps,
      };
      instance
        .acquireTokenSilent({
          scopes: protectedResources.updateValidatedSteps.scopes,
          account: account,
        })
        .then((response) => {
          callApiWithToken(
            response.accessToken,
            `${protectedResources.updateValidatedSteps.endpoint}`,
            "POST",
            data
          );
        })
        .catch((error) => {
          // in case if silent token acquisition fails, fallback to an interactive method
          if (error instanceof InteractionRequiredAuthError) {
            if (account && inProgress === "none") {
              instance
                .acquireTokenPopup({
                  scopes: protectedResources.updateValidatedSteps.scopes,
                })
                .then((response) => {
                  callApiWithToken(
                    response.accessToken,
                    `${protectedResources.updateValidatedSteps.endpoint}`,
                    "POST",
                    data
                  );
                })
                .catch((error) => console.log(error));
            }
          }
        });
    }
  }, [validatedSteps]);

  const maxSteps = steps[jobType]?.maxSteps ?? steps.default.maxSteps;
  let nextStep = currentStep + 1;
  let prevStep = currentStep - 1;

  if (jobType === "individuel" && currentStep === 2) {
    nextStep = 5;
  }
  if (jobType === "individuel" && currentStep === 5) {
    prevStep = 2;
  }

  if (
    (jobType === "gartner" ||
      jobType === "gartneriarbejder" ||
      jobType === "hk") &&
    currentStep === 3
  ) {
    nextStep = 5;
  }
  if (
    (jobType === "gartner" ||
      jobType === "gartneriarbejder" ||
      jobType === "hk") &&
    currentStep === 5
  ) {
    prevStep = 3;
  }

  if (!dataFetched) {
    return (
      <Layout showToolBar>
        <div>Indlæser</div>
      </Layout>
    );
  } else {
    return (
      <Layout showToolBar>
        <StepNavigation />
        <FormStep />
        <BtnNavigation>
          {currentStep > 0 && (
            <NavBtn type="button" onClick={() => updateStep(prevStep)}>
              &lt; Forrige
            </NavBtn>
          )}
          {currentStep < maxSteps && (
            <NavBtn type="button" onClick={() => updateStep(nextStep)}>
              Næste &gt;
            </NavBtn>
          )}
          {currentStep === maxSteps && (
            <NavBtn type="button" onClick={() => toggleModal("print")}>
              Udskriv
            </NavBtn>
          )}
        </BtnNavigation>
        <PrintModal
          jobType={jobType}
          isVisible={isVisible}
          toggleModal={toggleModal}
          activeModal={activeModal}
        />
      </Layout>
    );
  }
}

const NavBtn = styled.button`
  border-radius: 8px;
  background: ${(props) => props.theme.color.light_sea_green};
  color: #ffffff;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  transition: 0.3s background;
  &:only-child {
    margin-left: auto;
  }
  &:hover {
    background: ${(props) => props.theme.color.dark_electric_blue};
  }
  &:focus {
    outline: none;
  }
`;

const BtnNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5em 0;
`;

export default Apron;
